import * as ngCore from '@angular/core'
import type * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'

/** Modal for assigning a standard user to the organizations they work on */
@ngCore.Component({
  imports: [
    ds.button.Component,
    ds.input.select.multiple.Component,
    ds.input.select.single.Component,
    ds.layout.widget.Component,
    ds.overlay.centered.Component,
  ],
  selector: 'atn-space-lite-add-standard-user-modal',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.userAccess.standardUsers.Collection} */
  public readonly standardUsersEc = ngCore.inject(
    state.space.userAccess.standardUsers.Collection,
  )

  /** @see {@link state.space.userAccess.users.Collection} */
  private readonly _usersEc = ngCore.inject(
    state.space.userAccess.users.Collection,
  )

  /** @see {@link state.space.organizations.Feature} */
  private readonly _organizationsFeature = ngCore.inject(
    state.space.organizations.Feature,
  )

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /**
   * The organization entity users are assigned at, which is the deepest one in
   * the schema, e.g. Department
   */
  private readonly _leafOrganizationEntitySg = ngCore.computed(
    (schema = this._schemaFeature.selectors.schema.asSignal()) =>
      schema.tryGetLeafEntityOf(
        state.space.organizations.constants.DIMENSION_ID,
      ),
  )

  /** The items to display in the users dropdown */
  public readonly userSelectItemsSg = ngCore.computed<
    readonly ds.input.select.models.Item<ents.extra.space.User.Id>[]
  >(
    (
      users = this._usersEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
    ) =>
      users.map(user => ({
        label: `${user.id} - ${user.name}`,
        value: user.id,
      })),
  )

  /**
   * The name of the organization entity users are assigned at, which labels
   * the dropdown. For example, "Department".
   */
  public readonly organizationLabelSg = ngCore.computed(
    (leafOrganizationEntity = this._leafOrganizationEntitySg()) =>
      leafOrganizationEntity?.name ?? 'Organization',
  )

  /** The items to display in the organizations dropdown */
  public readonly organizationOptionsSg = ngCore.computed(
    (leafOrganizationEntity = this._leafOrganizationEntitySg()) => {
      const organizationsEc = leafOrganizationEntity
        ? this._organizationsFeature.selectors
            .getAllOrganizationEc(leafOrganizationEntity.id)
            .asSignal()
        : undefined

      return (
        organizationsEc?.plugins.cachePlugin.feature.selectors
          .getCachedEntities()
          .asSignal()
          .filter(TypeChecker.isNotNullish.raw)
          .map(org => ({ label: org.name, value: org.id })) ?? []
      )
    },
  )

  /** The user being assigned */
  public readonly selectedUserIdSg = ngCore.computed(
    (
      userAccess = this.standardUsersEc.plugins.currentEntityPlugin.feature.selectors
        .getCreationEntity()
        .asSignal(),
    ) => userAccess?.userId,
  )

  /** The organizations the user is being assigned to */
  public readonly selectedOrganizationIdsSg = ngCore.computed(
    (
      userAccess = this.standardUsersEc.plugins.currentEntityPlugin.feature.selectors
        .getCreationEntity()
        .asSignal(),
    ) => userAccess?.organizationIds ?? [],
  )

  /** Whether user can save the assignment */
  public readonly canConfirmSg = ngCore.computed(
    (
      selectedUserId = this.selectedUserIdSg(),
      selectedOrganizationIds = this.selectedOrganizationIdsSg(),
    ) => !!selectedUserId && selectedOrganizationIds.length > 0,
  )

  /** Stores the user being assigned */
  public selectedUserChangedHandler(
    selectedUserId: ents.extra.space.User.Id | undefined,
  ): void {
    this.standardUsersEc.plugins.editingPlugin.feature.actions.edit
      .from('Space.UserAccess.AddStandardUserModal.User.Changed')
      .dispatch({ edit: { input: { userId: selectedUserId } } })
  }

  /** Stores the organizations the user is being assigned to */
  public selectedOrganizationsChangedHandler(
    organizationIds: readonly ds.input.select.models.Item<ents.extra.space.Organization.Id>['value'][],
  ): void {
    this.standardUsersEc.plugins.editingPlugin.feature.actions.edit
      .from('Space.UserAccess.AddStandardUserModal.Organizations.Changed')
      .dispatch({
        edit: {
          input: {
            organizationIds: organizationIds.filter(
              TypeChecker.isNotNullish.raw,
            ),
          },
        },
      })
  }

  /** Assigns the user to the selected organizations */
  public saveClickHandler(): void {
    this.standardUsersEc.plugins.editingPlugin.feature.actions.submit
      .from('Space.UserAccess.AddStandardUserModal.Save.Clicked')
      .dispatch({ current: true })
  }

  /** Closes the modal without assigning anything */
  public cancelClickHandler(): void {
    this.standardUsersEc.plugins.editingPlugin.feature.actions.discard
      .from('Space.UserAccess.AddStandardUserModal.Cancel.Clicked')
      .dispatch({ current: true })
  }
}
